import type { ReactNode } from "react";

import type { FaireAdPerformancePeriodEntity } from "@faire/playground-entity-db/entity_types/adPerformancePeriod";
import type { AdPerformanceScope } from "@faire/playground-entity-db/entity_types/adPerformancePeriod";

import { monthWindowForRange } from "./constants/filters";
import { COPY } from "./copy";
import { PRODUCT_ROWS } from "./mockData";
import {
  getActiveProfileToken,
  getP8ActivityMonthLabels,
  getP8ActivityMonthlyTotals,
  getP8Campaigns,
  getP8ChartMonths,
  getP8ImpressionsPerSpendRatio,
  getP8MonthlyTotals,
  getP8OverviewPeriods,
  getP8Profile,
  getP8TableSegmentScale,
  getScopePeriods,
  type P8MonthlyTotals,
} from "./p8AdvertiserData";
import {
  getRealBrandAnchorLabel,
  getRealBrandCurrentPeriod,
  isRealBrandProfile,
} from "./realBrandData";
import { fillCopy } from "./renderCopy";
import type { AdsDateRange, InsightsCustomerSegment } from "./types";

const MONTH_LABEL = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "numeric",
});

const PLACEHOLDER_IMAGE_URL =
  "https://cdn.faire.com/fastly/cd023b381307ed3e3705704a6b6136873b3f683f4746a4a82e3810e3339b8d6b.png";

function seedRand(n: number) {
  const x = Math.sin(n * 9301 + 49297) * 10000;
  return x - Math.floor(x);
}

function monthLabelFromIso(isoDate: string): string {
  const [year, month] = isoDate.split("-").map(Number);
  return MONTH_LABEL.format(new Date(year, month - 1, 1));
}

function formatDisplayDate(isoDate: string): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
}

function getPeriodsForScope(
  scope: AdPerformanceScope
): FaireAdPerformancePeriodEntity[] {
  if (scope === "OVERVIEW") {
    return getP8OverviewPeriods();
  }
  return getScopePeriods(scope);
}

function findPeriodByMonth(
  periods: FaireAdPerformancePeriodEntity[],
  month: string
) {
  return periods.find(
    (period) => monthLabelFromIso(period.startDate) === month
  );
}

export function formatOverviewCurrency(amount: number): string {
  return `$${Math.round(amount).toLocaleString("en-US")}`;
}

export function formatOverviewCount(amount: number): string {
  return Math.round(amount).toLocaleString("en-US");
}

export function formatOverviewRoas(amount: number): string {
  return `${amount.toFixed(1)}x`;
}

export type P8OverviewKpi = {
  title: string;
  value: string;
  footers: string[];
  /**
   * Optional tooltip shown on hover/focus of the metric label. A node rather
   * than a string so a tooltip can carry a control, as the chart legend's
   * already does — the nodes themselves are assembled in the components, since
   * this file is data and holds no markup.
   */
  tooltip?: ReactNode;
  /**
   * Required when `tooltip` contains something to click — see the prop of the
   * same name on HeaderTooltip. Separate from `tooltip` being a node because
   * plenty of nodes are still just prose.
   */
  interactiveTooltip?: boolean;
};

/** Labels, tooltips, and footer templates for the four Overview header metrics. */
const OVERVIEW_KPI = COPY.overview.kpi;
const KPI_FOOTERS = OVERVIEW_KPI.footers;

/** New-customer share of ad sales, used to split sales-to-date into segments. */
function getNewSalesShare(): number {
  const overview = getP8Profile().overview;
  return overview.totalAdsSalesDollars > 0
    ? (overview.newCustomerAdsSalesDollars ?? 0) / overview.totalAdsSalesDollars
    : 0;
}

function getReorderSalesShare(): number {
  const overview = getP8Profile().overview;
  return overview.totalAdsSalesDollars > 0
    ? (overview.reorderAdsSalesDollars ?? 0) / overview.totalAdsSalesDollars
    : 0;
}

function easeOutCumulative(t: number): number {
  const clamped = Math.min(1, Math.max(0, t));
  return 1 - Math.pow(1 - clamped, 1.8);
}

/**
 * Portion of a cohort's realized continued sales attributed to a given order
 * month (continued ad sales organized by order month, not cohort month).
 */
function continuedSalesAttributedToOrderMonth(
  cohortMonthIndex: number,
  orderMonthIndex: number,
  continuedTotal: number,
  latestRealizedOrderMonthIndex: number
): number {
  if (
    continuedTotal <= 0 ||
    orderMonthIndex <= cohortMonthIndex ||
    orderMonthIndex > latestRealizedOrderMonthIndex
  ) {
    return 0;
  }

  const firstOrderMonth = cohortMonthIndex + 1;
  const lastOrderMonth = Math.min(
    latestRealizedOrderMonthIndex,
    cohortMonthIndex + 12
  );

  if (orderMonthIndex < firstOrderMonth || orderMonthIndex > lastOrderMonth) {
    return 0;
  }

  const span = lastOrderMonth - firstOrderMonth + 1;
  const step = orderMonthIndex - firstOrderMonth + 1;
  const cumulativeNow = easeOutCumulative(step / span);
  const cumulativePrev = easeOutCumulative((step - 1) / span);
  return continuedTotal * (cumulativeNow - cumulativePrev);
}

function buildContinuedSalesPointsForMonths(
  months: string[]
): P8ContinuedSalesPoint[] {
  const currentMonthIndex = getCurrentMonthIndex();

  if (isRealBrandProfile(getActiveProfileToken())) {
    const periodByMonth = new Map(
      getP8OverviewPeriods().map((period) => [
        monthLabelFromIso(period.startDate),
        period,
      ])
    );
    return months.map((month) => {
      const period = periodByMonth.get(month);
      const firstYearValue = period?.salesDollars ?? 0;
      const firstOrderSales = period?.firstOrderSalesDollars ?? firstYearValue;
      const salesToDate = period?.salesToDateDollars ?? firstOrderSales;
      return {
        month,
        firstOrderSales,
        continuedSales: Math.max(0, salesToDate - firstOrderSales),
        projectedFirstYearSales: Math.max(0, firstYearValue - salesToDate),
        adSpend: period?.adsSpendDollars ?? 0,
      };
    });
  }

  const activityTotals = getP8ActivityMonthlyTotals();
  return months.map((month) => {
    const totals = activityTotals.find((row) => row.month === month);
    const monthIndex = monthIndexFromLabel(month);
    const age = currentMonthIndex - monthIndex;
    const firstOrderSales = totals?.adSales ?? 0;

    return {
      month,
      firstOrderSales,
      ...modelContinuedSales(firstOrderSales, age),
      adSpend: totals?.adSpend ?? 0,
    };
  });
}

export type P8MonthlySalesStack = {
  month: string;
  new: number;
  reorder: number;
  previous: number;
};

/**
 * Monthly ad sales by source for the stacked production-style chart.
 *
 * - `new`: immediate ad sales from new-customer ads in the order month
 * - `reorder`: immediate ad sales from returning-customer ads in the order month
 * - `previous`: continued ad sales in the order month from customers acquired
 *   via new-customer ads in earlier months (order month vs. cohort month)
 */
export function getP8MonthlySalesBySource(
  chartMonths: string[] = getP8ChartMonths()
): P8MonthlySalesStack[] {
  const newPeriods = getPeriodsForScope("NEW_MARKETPLACE");
  const reorderPeriods = getPeriodsForScope("REORDER_MARKETPLACE");
  const monthlyTotals = getP8MonthlyTotals();
  const newShare = getNewSalesShare();
  const reorderShare = getReorderSalesShare();
  const latestRealizedOrderMonthIndex = getCurrentMonthIndex() - 1;

  const cohortSeries = buildContinuedSalesPointsForMonths(
    getP8ActivityMonthLabels()
  );
  // The monthly totals only cover closed months, so the in-progress month has to
  // come from the point the other charts plot for it. Without this the newest bar
  // is empty whenever the date range reaches that month.
  const inProgress = getInProgressChartPoint();

  return chartMonths.map((month) => {
    const orderMonthIndex = monthIndexFromLabel(month);
    const newPeriod = findPeriodByMonth(newPeriods, month);
    const reorderPeriod = findPeriodByMonth(reorderPeriods, month);
    const totals = monthlyTotals.find((row) => row.month === month);
    const hasSegmentData =
      newPeriod != null ||
      reorderPeriod != null ||
      newPeriod?.firstOrderSalesDollars != null ||
      reorderPeriod?.firstOrderSalesDollars != null;

    let newSales: number;
    let reorderSales: number;

    if (hasSegmentData) {
      newSales = segmentImmediateSales(newPeriod);
      reorderSales = segmentImmediateSales(reorderPeriod);
    } else {
      const firstOrder =
        totals?.adSales ??
        (inProgress?.month === month ? inProgress.firstOrderSales : 0);
      newSales = Math.round(firstOrder * newShare);
      reorderSales = Math.round(firstOrder * reorderShare);
    }

    const previous = Math.round(
      cohortSeries.reduce((sum, cohort) => {
        const cohortMonthIndex = monthIndexFromLabel(cohort.month);
        return (
          sum +
          continuedSalesAttributedToOrderMonth(
            cohortMonthIndex,
            orderMonthIndex,
            cohort.continuedSales,
            latestRealizedOrderMonthIndex
          )
        );
      }, 0)
    );

    return {
      month,
      new: newSales,
      reorder: reorderSales,
      previous,
    };
  });
}

export type P8TopClickedProduct = {
  id: string;
  name: string;
  clicks: number;
  imageColor: string;
  imageUrl: string;
};

export function getP8TopClickedProducts(limit = 5): P8TopClickedProduct[] {
  return [...PRODUCT_ROWS]
    .map((row) => ({
      id: row.id,
      name: row.name,
      clicks: Number(row.clicks.replace(/,/g, "")) || 0,
      imageColor: row.imageColor,
      imageUrl: PLACEHOLDER_IMAGE_URL,
    }))
    .sort((left, right) => right.clicks - left.clicks)
    .slice(0, limit);
}

export type P8ActivityRow = {
  key: string;
  /** Month index (year*12 + monthIdx) used to key the drilldown + sort. */
  monthIndex: number;
  periodLabel: string;
  periodDetail?: string;
  budget: string;
  showChangeBudget?: boolean;
  adSpend: string;
  adSpendDetail?: string;
  utilization: number | null;
  utilizationAsTag?: boolean;
  impressions: string;
  clicks: string;
  conversions: string;
  /** Numeric conversions (matches the formatted `conversions`) for range roll-ups. */
  conversionsCount: number;
  /** New-customer acquisitions within `conversionsCount`. */
  newConversionsCount: number;
  /** Returning-customer conversions within `conversionsCount`. */
  returningConversionsCount: number;
  /** Sales from the orders these conversions placed in their first month. */
  firstOrderSales: string;
  /** First-order sales plus continued reorders accrued since (modeled by cohort age). */
  salesToDate: string;
  /** Numeric sales-to-date (matches `salesToDate`) for range roll-ups. */
  salesToDateAmount: number;
  /** New-customer portion of sales-to-date. */
  newSalesToDateAmount: number;
  /** Returning-customer portion of sales-to-date. */
  returningSalesToDateAmount: number;
  /** Percent lift of salesToDate over firstOrderSales; null for the in-progress month. */
  salesToDateDeltaPct: number | null;
  /** Dollar value of continued sales accrued since first order; null for in-progress. */
  salesToDateDeltaDollars: number | null;
  firstOrderRoas: string;
  firstYearRoas: string;
  /**
   * Numeric first-year sales behind `firstYearRoas`. Needed because a range's
   * ROAS is its summed first-year sales over its summed spend, and the formatted
   * string only carries one decimal.
   */
  firstYearSalesAmount: number;
  /**
   * Whether first-year ROAS is still a projection (cohort < 12 months old).
   * Once the first year is complete it's realized and shown as plain text.
   */
  firstYearRoasProjected: boolean;
};

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** New-customer share of conversions, used to split synthetic monthly totals. */
function getNewConversionShare(): number {
  const overview = getP8Profile().overview;
  return overview.numTotalConversions > 0
    ? overview.numCustomersAcquired / overview.numTotalConversions
    : 0;
}

export function monthIndexFromLabel(label: string): number {
  const [mon = "", year = ""] = label.trim().split(/\s+/);
  const monthIdx = Math.max(0, MONTHS_SHORT.indexOf(mon));
  return Number(year) * 12 + monthIdx;
}

/** Month index of the in-progress month (one after the latest closed receipt). */
export function getCurrentMonthIndex(): number {
  const last = getP8OverviewPeriods().at(-1);
  if (last) {
    const [year, month] = last.startDate.split("-").map(Number);
    return year * 12 + (month - 1) + 1;
  }
  return 2026 * 12 + 6;
}

export const MAX_CONTINUATION = 0.85;
const CONTINUATION_PER_MONTH = 0.07;

/**
 * Modeled reorder continuation for a cohort of the given age (months since
 * acquisition). Ad-acquired customers keep reordering, so "sales to date" lifts
 * above first-order sales as the cohort ages. Real per-cohort curves aren't in
 * CAMPAIGN_PERIODS, so this is modeled from cohort age (see snowflakeQueries.ts).
 */
export function continuationForAgeMonths(ageMonths: number): number {
  if (ageMonths <= 0) {
    return 0;
  }
  return Math.min(MAX_CONTINUATION, ageMonths * CONTINUATION_PER_MONTH);
}

/**
 * Continued sales modeled from cohort age: what a cohort has realized so far
 * plus the remainder still expected by month 12. Shared so the month table's
 * first-year ROAS and the first-year charts can't drift apart.
 */
function modelContinuedSales(firstOrderSales: number, ageMonths: number) {
  const delta = continuationForAgeMonths(ageMonths);
  return {
    continuedSales: Math.round(firstOrderSales * delta),
    projectedFirstYearSales:
      ageMonths >= 12
        ? 0
        : Math.round(firstOrderSales * Math.max(0, MAX_CONTINUATION - delta)),
  };
}

/**
 * Total revenue a cohort is expected to generate within 12 months of
 * acquisition: immediate sales plus continued sales, realized and forecasted.
 * This is the numerator of first-year ROAS.
 */
function modeledFirstYearSales(
  firstOrderSales: number,
  ageMonths: number
): number {
  const modeled = modelContinuedSales(firstOrderSales, ageMonths);
  return (
    firstOrderSales + modeled.continuedSales + modeled.projectedFirstYearSales
  );
}

function mapSyntheticActivityRow(
  month: string,
  totals: P8MonthlyTotals,
  budgetDollars: number
): P8ActivityRow {
  const impressions = Math.round(
    totals.adSpend * getP8ImpressionsPerSpendRatio()
  );
  const monthIndex = monthIndexFromLabel(month);
  const age = getCurrentMonthIndex() - monthIndex;
  const delta = continuationForAgeMonths(age);
  const firstOrderSales = totals.adSales;
  const salesToDate = Math.round(firstOrderSales * (1 + delta));
  const firstOrderRoas =
    totals.adSpend > 0 ? firstOrderSales / totals.adSpend : 0;
  const firstYearSales = modeledFirstYearSales(firstOrderSales, age);
  const firstYearRoas =
    totals.adSpend > 0 ? firstYearSales / totals.adSpend : 0;
  const conversionsCount = totals.conversions;
  // A month with no ad sales and no conversions has no acquired cohort, so its
  // sales-to-date lift and first-year ROAS are undefined rather than zero.
  const hasActivity = firstOrderSales > 0 || conversionsCount > 0;
  const newConversionsCount = Math.round(
    conversionsCount * getNewConversionShare()
  );
  const returningConversionsCount = Math.max(
    0,
    conversionsCount - newConversionsCount
  );
  const newSalesToDateAmount = Math.round(salesToDate * getNewSalesShare());
  const returningSalesToDateAmount = Math.max(
    0,
    salesToDate - newSalesToDateAmount
  );

  return {
    key: `synthetic-${month.replace(/\s/g, "-").toLowerCase()}`,
    monthIndex,
    periodLabel: month,
    budget: formatOverviewCurrency(budgetDollars),
    adSpend: formatOverviewCurrency(totals.adSpend),
    utilization: budgetDollars
      ? Math.min(0.99, totals.adSpend / budgetDollars)
      : null,
    impressions: formatOverviewCount(impressions),
    clicks: formatOverviewCount(totals.clicks),
    conversions: formatOverviewCount(conversionsCount),
    conversionsCount,
    newConversionsCount,
    returningConversionsCount,
    firstOrderSales: formatOverviewCurrency(firstOrderSales),
    salesToDate: formatOverviewCurrency(salesToDate),
    salesToDateAmount: salesToDate,
    newSalesToDateAmount,
    returningSalesToDateAmount,
    salesToDateDeltaPct:
      hasActivity && age > 0 ? Math.round(delta * 100) : null,
    salesToDateDeltaDollars:
      hasActivity && age > 0 ? salesToDate - firstOrderSales : null,
    firstOrderRoas: formatOverviewRoas(firstOrderRoas),
    firstYearRoas: hasActivity ? formatOverviewRoas(firstYearRoas) : "—",
    firstYearSalesAmount: hasActivity ? firstYearSales : 0,
    firstYearRoasProjected: hasActivity && age < 12,
  };
}

function mapReceiptRow(period: FaireAdPerformancePeriodEntity): P8ActivityRow {
  const newConversionsCount = period.newCustomersAcquired ?? 0;
  const returningConversionsCount = period.reorderConversions ?? 0;
  const conversions = newConversionsCount + returningConversionsCount;
  const monthIndex = monthIndexFromLabel(monthLabelFromIso(period.startDate));
  const age = getCurrentMonthIndex() - monthIndex;

  // Best-estimate first-year value drives first-year ROAS.
  const firstYearValue = period.salesDollars ?? 0;
  // Real brands carry actual first-order + sales-to-date; synthetic/legacy
  // receipts model the continuation curve from cohort age.
  const hasRealSales = period.firstOrderSalesDollars != null;
  const firstOrderSales = hasRealSales
    ? (period.firstOrderSalesDollars ?? 0)
    : firstYearValue;
  const salesToDate = hasRealSales
    ? (period.salesToDateDollars ?? firstOrderSales)
    : Math.round(firstOrderSales * (1 + continuationForAgeMonths(age)));
  // A month with no ad sales and no conversions has no acquired cohort, so its
  // sales-to-date lift and first-year ROAS are undefined rather than zero.
  const hasActivity =
    firstOrderSales > 0 || conversions > 0 || firstYearValue > 0;
  const firstOrderRoas =
    period.adsSpendDollars > 0 ? firstOrderSales / period.adsSpendDollars : 0;
  const firstYearRoasValue = hasRealSales
    ? firstYearValue
    : modeledFirstYearSales(firstOrderSales, age);
  const firstYearRoas =
    period.adsSpendDollars > 0
      ? firstYearRoasValue / period.adsSpendDollars
      : 0;
  const deltaDollars = Math.max(0, salesToDate - firstOrderSales);
  const deltaPct =
    firstOrderSales > 0
      ? Math.round((deltaDollars / firstOrderSales) * 100)
      : 0;
  const newSalesToDateAmount = Math.round(salesToDate * getNewSalesShare());
  const returningSalesToDateAmount = Math.max(
    0,
    salesToDate - newSalesToDateAmount
  );

  return {
    key: period.token,
    monthIndex,
    periodLabel: monthLabelFromIso(period.startDate),
    budget: formatOverviewCurrency(period.budgetDollars ?? 0),
    adSpend: formatOverviewCurrency(period.adsSpendDollars),
    utilization: period.budgetUtilization ?? null,
    impressions: formatOverviewCount(period.impressions),
    clicks: formatOverviewCount(period.clicks),
    conversions: formatOverviewCount(conversions),
    conversionsCount: conversions,
    newConversionsCount,
    returningConversionsCount,
    firstOrderSales: formatOverviewCurrency(firstOrderSales),
    salesToDate: formatOverviewCurrency(salesToDate),
    salesToDateAmount: salesToDate,
    newSalesToDateAmount,
    returningSalesToDateAmount,
    salesToDateDeltaPct: hasActivity && deltaDollars > 0 ? deltaPct : null,
    salesToDateDeltaDollars:
      hasActivity && deltaDollars > 0 ? deltaDollars : null,
    firstOrderRoas: formatOverviewRoas(firstOrderRoas),
    firstYearRoas: hasActivity ? formatOverviewRoas(firstYearRoas) : "—",
    firstYearSalesAmount: hasActivity ? firstYearRoasValue : 0,
    firstYearRoasProjected: hasActivity && age < 12,
  };
}

/** Partial in-progress month for real brands (null / empty when paused). */
function getRealBrandCurrentActivityRows(): P8ActivityRow[] {
  const activeToken = getActiveProfileToken();
  const current = getRealBrandCurrentPeriod(activeToken);
  if (!current) {
    return [];
  }
  const newConversionsCount = current.newCustomersAcquired ?? 0;
  const returningConversionsCount = current.reorderConversions ?? 0;
  const conversions = newConversionsCount + returningConversionsCount;
  const firstYearValue = current.salesDollars ?? 0;
  const firstOrderSales = current.firstOrderSalesDollars ?? firstYearValue;
  const salesToDate = current.salesToDateDollars ?? firstOrderSales;
  const hasActivity =
    firstOrderSales > 0 || conversions > 0 || firstYearValue > 0;
  const firstOrderRoas =
    current.adsSpendDollars > 0 ? firstOrderSales / current.adsSpendDollars : 0;
  const firstYearRoas =
    current.adsSpendDollars > 0 ? firstYearValue / current.adsSpendDollars : 0;
  return [
    {
      key: `current-${current.startDate}`,
      monthIndex: monthIndexFromLabel(monthLabelFromIso(current.startDate)),
      periodLabel: monthLabelFromIso(current.startDate),
      periodDetail: "Active",
      budget: formatOverviewCurrency(current.budgetDollars ?? 0),
      adSpend: formatOverviewCurrency(current.adsSpendDollars),
      adSpendDetail: `As of ${getRealBrandAnchorLabel(activeToken).replace(/,?\s*\d{4}$/, "")}`,
      utilization: current.budgetUtilization ?? null,
      utilizationAsTag: true,
      impressions: formatOverviewCount(current.impressions),
      clicks: formatOverviewCount(current.clicks),
      conversions: formatOverviewCount(conversions),
      conversionsCount: conversions,
      newConversionsCount,
      returningConversionsCount,
      firstOrderSales: formatOverviewCurrency(firstOrderSales),
      salesToDate: formatOverviewCurrency(salesToDate),
      salesToDateAmount: salesToDate,
      newSalesToDateAmount: Math.round(salesToDate * getNewSalesShare()),
      returningSalesToDateAmount: Math.max(
        0,
        salesToDate - Math.round(salesToDate * getNewSalesShare())
      ),
      salesToDateDeltaPct: null,
      salesToDateDeltaDollars: null,
      firstOrderRoas: formatOverviewRoas(firstOrderRoas),
      firstYearRoas: hasActivity ? formatOverviewRoas(firstYearRoas) : "—",
      firstYearSalesAmount: hasActivity ? firstYearValue : 0,
      firstYearRoasProjected: hasActivity,
    },
  ];
}

/** Partial July 2026 month scaled from June receipt metrics (interview date Jul 10). */
export function getP8CurrentActivityRows(): P8ActivityRow[] {
  if (isRealBrandProfile(getActiveProfileToken())) {
    return getRealBrandCurrentActivityRows();
  }
  const june = findPeriodByMonth(
    getP8OverviewPeriods(),
    monthLabelFromIso("2026-06-01")
  );
  if (!june) {
    return [];
  }

  const profile = getP8Profile();
  const currentBudgetDollars = profile.budget.totalBudgetDollars;
  const progress = 10 / 30;
  const spend = Math.round(june.adsSpendDollars * progress);
  const sales = Math.round((june.salesDollars ?? 0) * progress);
  const roas = spend > 0 ? sales / spend : 0;
  const newConversionsCount = Math.round(
    (june.newCustomersAcquired ?? 0) * progress
  );
  const returningConversionsCount = Math.round(
    (june.reorderConversions ?? 0) * progress
  );
  const conversions = newConversionsCount + returningConversionsCount;

  return [
    {
      key: "current-july-2026",
      monthIndex: monthIndexFromLabel("Jul 2026"),
      periodLabel: "Jul 2026",
      periodDetail: "Active",
      budget: formatOverviewCurrency(currentBudgetDollars),
      adSpend: formatOverviewCurrency(spend),
      adSpendDetail: "(as of Jul 10)",
      utilization: currentBudgetDollars
        ? Math.min(0.99, spend / currentBudgetDollars)
        : null,
      utilizationAsTag: true,
      impressions: formatOverviewCount(Math.round(june.impressions * progress)),
      clicks: formatOverviewCount(Math.round(june.clicks * progress)),
      conversions: formatOverviewCount(conversions),
      conversionsCount: conversions,
      newConversionsCount,
      returningConversionsCount,
      firstOrderSales: formatOverviewCurrency(sales),
      salesToDate: formatOverviewCurrency(sales),
      salesToDateAmount: sales,
      newSalesToDateAmount: Math.round(sales * getNewSalesShare()),
      returningSalesToDateAmount: Math.max(
        0,
        sales - Math.round(sales * getNewSalesShare())
      ),
      salesToDateDeltaPct: null,
      salesToDateDeltaDollars: null,
      firstOrderRoas: formatOverviewRoas(roas),
      firstYearRoas: formatOverviewRoas(roas),
      firstYearSalesAmount: sales,
      firstYearRoasProjected: true,
    },
  ];
}

export function getP8PreviousActivityRows(): P8ActivityRow[] {
  const periodByMonth = new Map(
    getP8OverviewPeriods().map((period) => [
      monthLabelFromIso(period.startDate),
      period,
    ])
  );
  const totalsByMonth = new Map(
    getP8ActivityMonthlyTotals().map((totals) => [totals.month, totals])
  );
  const budgetDollars = getP8Profile().budget.totalBudgetDollars;

  return getP8ActivityMonthLabels()
    .slice()
    .reverse()
    .map((month) => {
      const receipt = periodByMonth.get(month);
      if (receipt) {
        return mapReceiptRow(receipt);
      }
      const totals = totalsByMonth.get(month);
      if (!totals) {
        return null;
      }
      return mapSyntheticActivityRow(month, totals, budgetDollars);
    })
    .filter((row): row is P8ActivityRow => row != null);
}

/** Single "Ad performance by month" table: in-progress month first, then closed months. */
export function getP8AdPerformanceByMonthRows(): P8ActivityRow[] {
  return [...getP8CurrentActivityRows(), ...getP8PreviousActivityRows()];
}

/** Digits back out of an already-formatted cell ("$1,234", "1,234", "5.1x"). */
function parseFormattedAmount(value: string): number {
  const parsed = Number(value.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Re-cut the month table for a single customer segment. Conversions and total ad
 * sales come from the per-segment splits the rows already carry; spend, traffic,
 * and immediate sales are derived from the profile's new-vs-reorder shares. Both
 * ROAS columns are rescaled by the row's own sales and spend shares so a segment
 * row stays internally consistent with the numbers beside it.
 */
export function applySegmentToActivityRows(
  rows: P8ActivityRow[],
  segment: InsightsCustomerSegment
): P8ActivityRow[] {
  if (segment === "all-customers") {
    return rows;
  }

  const isNew = segment === "new-customers";
  const spendScale = getP8TableSegmentScale(segment, "adSpend");
  const impressionsScale = getP8TableSegmentScale(segment, "impressions");
  const clicksScale = getP8TableSegmentScale(segment, "clicks");

  return rows.map((row) => {
    const salesToDate = isNew
      ? row.newSalesToDateAmount
      : row.returningSalesToDateAmount;
    // Reorders land in the month they happen, so a returning cohort has no
    // continued sales and its immediate and total ad sales are the same number.
    // New customers keep the row's own immediate-to-total lift, which also
    // guarantees immediate can never exceed total.
    const liftRatio = isNew ? 1 + (row.salesToDateDeltaPct ?? 0) / 100 : 1;
    const firstOrderSales = Math.round(salesToDate / liftRatio);
    const conversionsCount = isNew
      ? row.newConversionsCount
      : row.returningConversionsCount;
    const adSpend = Math.round(parseFormattedAmount(row.adSpend) * spendScale);
    const salesShare =
      row.salesToDateAmount > 0 ? salesToDate / row.salesToDateAmount : 0;
    const roasScale = spendScale > 0 ? salesShare / spendScale : 0;
    const segmentRoas = formatOverviewRoas(
      adSpend > 0 ? firstOrderSales / adSpend : 0
    );

    return {
      ...row,
      adSpend: formatOverviewCurrency(adSpend),
      impressions: formatOverviewCount(
        Math.round(parseFormattedAmount(row.impressions) * impressionsScale)
      ),
      clicks: formatOverviewCount(
        Math.round(parseFormattedAmount(row.clicks) * clicksScale)
      ),
      conversions: formatOverviewCount(conversionsCount),
      conversionsCount,
      firstOrderSales: formatOverviewCurrency(firstOrderSales),
      salesToDate: formatOverviewCurrency(salesToDate),
      salesToDateAmount: salesToDate,
      salesToDateDeltaPct: isNew ? row.salesToDateDeltaPct : null,
      salesToDateDeltaDollars:
        isNew && row.salesToDateDeltaDollars != null
          ? Math.max(0, salesToDate - firstOrderSales)
          : null,
      firstOrderRoas: segmentRoas,
      // A reorder ad's sale lands in the month of the click and never continues,
      // so its first year is already over: the first-year total is the immediate
      // total and the first-year return is the immediate return. The columns
      // report those equal figures rather than going blank, since the metric is
      // counted for reorder ads under All ad types too — it just has nothing
      // extra in it. Both are final, so the projected tag comes off as well.
      //
      // New customers scale by the segment's share of sales, not by roasScale,
      // so a segment's rolled-up ROAS stays first-year sales over spend rather
      // than compounding the spend scale twice.
      firstYearRoas: isNew
        ? row.firstYearRoas === "—"
          ? "—"
          : formatOverviewRoas(
              parseFormattedAmount(row.firstYearRoas) * roasScale
            )
        : segmentRoas,
      firstYearSalesAmount: isNew
        ? Math.round(row.firstYearSalesAmount * salesShare)
        : firstOrderSales,
      firstYearRoasProjected: isNew ? row.firstYearRoasProjected : false,
    };
  });
}

export type ConversionsSummary = {
  total: number;
  new: number;
  returning: number;
};

/** Roll up conversions (total + new/returning) across a set of month rows. */
export function sumConversions(rows: P8ActivityRow[]): ConversionsSummary {
  return rows.reduce<ConversionsSummary>(
    (acc, row) => ({
      total: acc.total + row.conversionsCount,
      new: acc.new + row.newConversionsCount,
      returning: acc.returning + row.returningConversionsCount,
    }),
    { total: 0, new: 0, returning: 0 }
  );
}

/**
 * Conversions KPI whose value is the sum of the given rows (i.e. the visible
 * range).
 *
 * `splitFooters` is off when the rows have already been cut to a single ad type:
 * the split would then restate the value against a zero. The row-level
 * new/returning counts aren't rescaled by that cut, so the footers would also be
 * quoting the unfiltered program.
 */
export function getConversionsKpiFromRows(
  rows: P8ActivityRow[],
  splitFooters = true
): P8OverviewKpi {
  const sums = sumConversions(rows);
  return {
    title: OVERVIEW_KPI.conversionsTitle,
    value: formatOverviewCount(sums.total),
    footers: splitFooters
      ? [
          fillCopy(KPI_FOOTERS.conversionsNew, {
            value: formatOverviewCount(sums.new),
          }),
          fillCopy(KPI_FOOTERS.conversionsReorder, {
            value: formatOverviewCount(sums.returning),
          }),
        ]
      : [],
    tooltip: OVERVIEW_KPI.conversions,
  };
}

export type SalesSummary = {
  total: number;
  new: number;
  returning: number;
};

/** Roll up sales-to-date (total + new/returning) across a set of month rows. */
export function sumSalesToDate(rows: P8ActivityRow[]): SalesSummary {
  return rows.reduce<SalesSummary>(
    (acc, row) => ({
      total: acc.total + row.salesToDateAmount,
      new: acc.new + row.newSalesToDateAmount,
      returning: acc.returning + row.returningSalesToDateAmount,
    }),
    { total: 0, new: 0, returning: 0 }
  );
}

/**
 * A month's continued ad sales: what its cohort has bought since the acquisition
 * month. Prefers the row's own figure, and otherwise takes the same difference
 * the continued-sales charts plot, so the column and the chart above it can't
 * disagree. Months carrying no lift — the one still in progress, and any whose
 * cohort has not reordered — fall to that second path rather than reading as
 * blank.
 */
/**
 * A month's ad spend as a share of its budget. Distinct from the row's own
 * `utilization`, which is the platform's figure for the whole month's budget
 * against all ad types and carries a forecast for months still running. This is
 * a plain division of what the visible rows report, so it holds up when the rows
 * have been cut to one ad type: budget is set for the month rather than per ad
 * type, so the numerator narrows while the denominator stays put.
 *
 * Null when there's no budget to divide by, which would otherwise read as 0%.
 */
export function getRowBudgetShare(row: P8ActivityRow): number | null {
  const budget = parseFormattedAmount(row.budget);
  if (budget <= 0) {
    return null;
  }
  return parseFormattedAmount(row.adSpend) / budget;
}

export function getRowContinuedSales(row: P8ActivityRow): number {
  if (row.salesToDateDeltaDollars != null) {
    return row.salesToDateDeltaDollars;
  }
  return Math.max(
    0,
    row.salesToDateAmount - parseFormattedAmount(row.firstOrderSales)
  );
}

/**
 * Total ad sales KPI whose value is the summed sales-to-date of the given rows
 * (visible range). See getConversionsKpiFromRows for `splitFooters`.
 */
export function getAdSalesKpiFromRows(
  rows: P8ActivityRow[],
  splitFooters = true
): P8OverviewKpi {
  const sums = sumSalesToDate(rows);
  return {
    title: OVERVIEW_KPI.totalAdSalesTitle,
    value: formatOverviewCurrency(sums.total),
    footers: splitFooters
      ? [
          fillCopy(KPI_FOOTERS.adSalesNew, {
            value: formatOverviewCurrency(sums.new),
          }),
          fillCopy(KPI_FOOTERS.adSalesReorder, {
            value: formatOverviewCurrency(sums.returning),
          }),
        ]
      : [],
    tooltip: OVERVIEW_KPI.totalAdSales,
  };
}

export type SpendSummary = {
  total: number;
  new: number;
  returning: number;
};

/**
 * Roll up ad spend across a set of month rows. Rows carry spend only as
 * formatted currency, but formatOverviewCurrency rounds to whole dollars, so
 * reading it back is exact. The new/returning split follows the profile's own
 * spend shares — the same ones the month table's segment filter uses.
 */
export function sumAdSpend(rows: P8ActivityRow[]): SpendSummary {
  const total = rows.reduce(
    (sum, row) => sum + parseFormattedAmount(row.adSpend),
    0
  );
  const newSpend = Math.round(
    total * getP8TableSegmentScale("new-customers", "adSpend")
  );
  return { total, new: newSpend, returning: Math.max(0, total - newSpend) };
}

/**
 * Ad spend KPI whose value is the summed spend of the given rows (visible
 * range). See getConversionsKpiFromRows for `splitFooters`.
 */
export function getAdSpendKpiFromRows(
  rows: P8ActivityRow[],
  splitFooters = true
): P8OverviewKpi {
  const sums = sumAdSpend(rows);
  return {
    title: OVERVIEW_KPI.adSpendTitle,
    value: formatOverviewCurrency(sums.total),
    footers: splitFooters
      ? [
          fillCopy(KPI_FOOTERS.adSpendNew, {
            value: formatOverviewCurrency(sums.new),
          }),
          fillCopy(KPI_FOOTERS.adSpendReorder, {
            value: formatOverviewCurrency(sums.returning),
          }),
        ]
      : [],
    tooltip: OVERVIEW_KPI.adSpend,
  };
}

/**
 * First-year ROAS KPI for the given rows: summed first-year sales over summed
 * spend, rather than an average of the monthly ratios, so a heavy month carries
 * proportional weight instead of counting the same as a quiet one.
 *
 * See getConversionsKpiFromRows for `splitFooters`.
 */
export function getFirstYearRoasKpiFromRows(
  rows: P8ActivityRow[],
  splitFooters = true
): P8OverviewKpi {
  const spend = sumAdSpend(rows);
  const firstYearSales = rows.reduce(
    (sum, row) => sum + row.firstYearSalesAmount,
    0
  );
  const newSales = Math.round(firstYearSales * getNewSalesShare());
  const returningSales = Math.max(0, firstYearSales - newSales);
  // A range the advertiser didn't spend in has no ROAS to report, so it gets the
  // same em dash the month rows use rather than a 0.0x that reads as a real loss.
  const roas = (sales: number, cost: number): string =>
    cost > 0 ? formatOverviewRoas(sales / cost) : "—";

  return {
    title: OVERVIEW_KPI.firstYearRoasTitle,
    value: roas(firstYearSales, spend.total),
    footers: splitFooters
      ? [
          fillCopy(KPI_FOOTERS.firstYearRoasNew, {
            value: roas(newSales, spend.new),
          }),
          fillCopy(KPI_FOOTERS.firstYearRoasReorder, {
            value: roas(returningSales, spend.returning),
          }),
        ]
      : [],
    tooltip: OVERVIEW_KPI.firstYearRoas,
  };
}

export type P8MonthlyAdSalesAndSpend = {
  month: string;
  firstOrderSales: number;
  adSpend: number;
};

/** Line-chart series: first-order sales vs ad spend across the chart window. */
export function getP8MonthlyAdSalesAndSpend(): P8MonthlyAdSalesAndSpend[] {
  return getP8MonthlyTotals().map((totals) => ({
    month: totals.month,
    firstOrderSales: totals.adSales,
    adSpend: totals.adSpend,
  }));
}

export type P8ContinuedSalesPoint = {
  month: string;
  firstOrderSales: number;
  continuedSales: number;
  projectedFirstYearSales: number;
  /** Spend that acquired this cohort, for the ad-sales-and-spend and ROAS charts. */
  adSpend: number;
};

/**
 * Stacked series showing, per acquisition month, the first-order sales, the
 * continued reorder sales accrued so far, and the still-projected first-year
 * remainder. Continuation is modeled by cohort age (see continuationForAgeMonths).
 */
export function getP8ContinuedSalesSeries(): P8ContinuedSalesPoint[] {
  return buildContinuedSalesPointsForMonths(getP8ChartMonths());
}

/**
 * How many months the charts plot for Lifetime. A whole program can run past
 * thirty months, which no half-width bar chart renders legibly, so Lifetime keeps
 * the trailing window the charts have always shown while the headline metrics
 * above them roll up everything. The paging arrows signal there is more behind.
 */
const LIFETIME_CHART_MONTHS = 13;

/** Index → "Mon YYYY". The inverse of monthIndexFromLabel. */
function chartMonthLabel(monthIndex: number): string {
  return `${MONTHS_SHORT[monthIndex % 12]} ${Math.floor(monthIndex / 12)}`;
}

/**
 * The in-progress month as a chart point, taken from the row the month table
 * shows for it rather than the monthly totals, which only cover closed months.
 * That keeps the newest bar and the newest table row reporting one set of
 * numbers.
 *
 * The charts need this month because the date range anchors on it: without it,
 * "This quarter" would have nothing to plot early in a quarter.
 */
function getInProgressChartPoint(): P8ContinuedSalesPoint | null {
  const row = getP8CurrentActivityRows()[0];
  if (!row) {
    return null;
  }
  return {
    month: chartMonthLabel(row.monthIndex),
    firstOrderSales: parseFormattedAmount(row.firstOrderSales),
    continuedSales: getRowContinuedSales(row),
    projectedFirstYearSales: Math.max(
      0,
      row.firstYearSalesAmount - row.salesToDateAmount
    ),
    adSpend: parseFormattedAmount(row.adSpend),
  };
}

/** Every month the charts can reach, oldest first, in-progress month last. */
function getAllChartPoints(): P8ContinuedSalesPoint[] {
  const closed = buildContinuedSalesPointsForMonths(getP8ActivityMonthLabels());
  const inProgress = getInProgressChartPoint();
  return inProgress ? [...closed, inProgress] : closed;
}

/** How many months the charts could show at most, for the paging affordance. */
export function getChartMonthCapacity(): number {
  return getAllChartPoints().length;
}

/**
 * Cohort series scoped to a date range, so a three-month range plots three
 * months. Anchored on the in-progress month — the same anchor the headline
 * metrics use — so one filter can't mean two different periods on one page.
 */
export function getP8ContinuedSalesSeriesForRange(
  range: AdsDateRange
): P8ContinuedSalesPoint[] {
  const all = getAllChartPoints();
  const window = monthWindowForRange(range, getCurrentMonthIndex());
  if (!window) {
    return all.slice(-LIFETIME_CHART_MONTHS);
  }
  return all.filter((point) => {
    const index = monthIndexFromLabel(point.month);
    return index >= window.from && index <= window.to;
  });
}

/**
 * The same series cut to one ad type, scaled by the profile's own sales and
 * spend shares — the shares the month table and the headline metrics use, so the
 * chart can't disagree with the figures beside it.
 *
 * Reorder ads keep no continued sales at all. A reorder lands in the month it
 * happens, so there is nothing that continues past it and nothing left to
 * forecast; both come back zero rather than a scaled-down share, and the charts
 * drop those series instead of plotting flat nothing.
 */
export function applySegmentToContinuedSales(
  points: P8ContinuedSalesPoint[],
  segment: InsightsCustomerSegment
): P8ContinuedSalesPoint[] {
  if (segment === "all-customers") {
    return points;
  }

  const salesScale = getP8TableSegmentScale(segment, "adSales");
  const spendScale = getP8TableSegmentScale(segment, "adSpend");
  const continues = segment === "new-customers";

  return points.map((point) => ({
    ...point,
    firstOrderSales: Math.round(point.firstOrderSales * salesScale),
    continuedSales: continues ? point.continuedSales : 0,
    projectedFirstYearSales: continues ? point.projectedFirstYearSales : 0,
    adSpend: Math.round(point.adSpend * spendScale),
  }));
}

/**
 * Whether an ad type has a first year to report: continued sales, a first-year
 * total, and a first-year ROAS. Reorder ads don't, for the reason above.
 */
export function segmentHasFirstYear(segment: InsightsCustomerSegment): boolean {
  return segment !== "returning-customers";
}

export type P8SplitImmediateSalesPoint = {
  month: string;
  newCustomerSales: number;
  retentionSales: number;
  adSpend: number;
};

function segmentImmediateSales(
  period?: FaireAdPerformancePeriodEntity
): number {
  if (!period) {
    return 0;
  }
  return period.firstOrderSalesDollars ?? period.salesDollars ?? 0;
}

/** Stacked new vs retention immediate ad sales with an ad-spend overlay line. */
export function getP8SplitImmediateSalesAndSpend(): P8SplitImmediateSalesPoint[] {
  const chartMonths = getP8ChartMonths();
  const overviewPeriods = getP8OverviewPeriods();
  const newPeriods = getPeriodsForScope("NEW_MARKETPLACE");
  const reorderPeriods = getPeriodsForScope("REORDER_MARKETPLACE");
  const monthlyTotals = getP8MonthlyTotals();
  const newShare = getNewSalesShare();

  return chartMonths.map((month) => {
    const overviewPeriod = findPeriodByMonth(overviewPeriods, month);
    const newPeriod = findPeriodByMonth(newPeriods, month);
    const reorderPeriod = findPeriodByMonth(reorderPeriods, month);
    const totals = monthlyTotals.find((row) => row.month === month);
    const hasSegmentData =
      newPeriod != null ||
      reorderPeriod != null ||
      overviewPeriod?.firstOrderSalesDollars != null;

    if (hasSegmentData) {
      return {
        month,
        newCustomerSales: segmentImmediateSales(newPeriod),
        retentionSales: segmentImmediateSales(reorderPeriod),
        adSpend: overviewPeriod?.adsSpendDollars ?? totals?.adSpend ?? 0,
      };
    }

    const firstOrder = totals?.adSales ?? 0;
    const newCustomerSales = Math.round(firstOrder * newShare);
    return {
      month,
      newCustomerSales,
      retentionSales: Math.max(0, firstOrder - newCustomerSales),
      adSpend: totals?.adSpend ?? 0,
    };
  });
}

export type P8CumulativeFirstYearPoint = {
  monthsSinceAcquisition: number;
  immediateSales: number;
  continuedActual: number;
  continuedForecast: number;
  /** Total spend across the same cohorts, flat because spend lands at acquisition. */
  adSpend: number;
};

/**
 * Cumulative first-year curve totalled across every acquisition cohort in the
 * chart window, indexed by cohort maturity rather than calendar month.
 *
 * The totals reconcile with the monthly first-year chart: immediate sales are
 * flat at the summed first-order sales (they all land in the acquisition month),
 * and at month 12 continued-actual and continued-forecast equal the summed
 * per-cohort figures shown there.
 */
export function getP8CumulativeFirstYearSeries(
  cohorts: P8ContinuedSalesPoint[] = getP8ContinuedSalesSeries()
): P8CumulativeFirstYearPoint[] {
  const currentMonthIndex = getCurrentMonthIndex();

  const immediateTotal = cohorts.reduce(
    (sum, cohort) => sum + cohort.firstOrderSales,
    0
  );
  const spendTotal = cohorts.reduce((sum, cohort) => sum + cohort.adSpend, 0);

  return Array.from({ length: 13 }, (_, k) => {
    const maturityShare = easeOutCumulative(k / 12);
    let actualSum = 0;
    let forecastSum = 0;

    for (const cohort of cohorts) {
      const age = Math.min(
        Math.max(currentMonthIndex - monthIndexFromLabel(cohort.month), 0),
        12
      );
      const realizedShare = easeOutCumulative(age / 12);

      // Realized continued sales accrue along the standard easing curve,
      // normalized so the cohort hits its actual to-date total at its current
      // age, then flatten — nothing beyond today is realized yet.
      const actual =
        cohort.continuedSales > 0 && realizedShare > 0
          ? cohort.continuedSales * Math.min(1, maturityShare / realizedShare)
          : 0;

      // The forecast is what's already realized plus the remainder still
      // expected, eased across the months the cohort has left to mature. That
      // keeps the forecast at or above actual at every maturity step.
      const remaining = age < 12 ? 12 - age : 0;
      const remainderShare =
        remaining > 0
          ? easeOutCumulative((k - age) / remaining)
          : k >= 12
            ? 1
            : 0;

      actualSum += actual;
      forecastSum += actual + cohort.projectedFirstYearSales * remainderShare;
    }

    return {
      monthsSinceAcquisition: k,
      immediateSales: Math.round(immediateTotal),
      continuedActual: Math.round(actualSum),
      continuedForecast: Math.round(forecastSum),
      adSpend: Math.round(spendTotal),
    };
  });
}

export type P8MonthlyRoasPoint = {
  month: string;
  immediateRoas: number;
  firstYearRoas: number;
};

/**
 * Monthly immediate ROAS alongside the first-year ROAS each month's cohort is
 * on track to reach once it has matured for a full 12 months.
 *
 * Built from a continued-sales series rather than fetching its own, so a caller
 * that has cut those points to one ad type gets a ROAS over the same numbers.
 */
export function getP8MonthlyRoasSeries(
  points: P8ContinuedSalesPoint[] = getP8ContinuedSalesSeries()
): P8MonthlyRoasPoint[] {
  return points.map((cohort) => {
    const firstYearSales =
      cohort.firstOrderSales +
      cohort.continuedSales +
      cohort.projectedFirstYearSales;
    return {
      month: cohort.month,
      immediateRoas:
        cohort.adSpend > 0 ? cohort.firstOrderSales / cohort.adSpend : 0,
      firstYearRoas: cohort.adSpend > 0 ? firstYearSales / cohort.adSpend : 0,
    };
  });
}

/**
 * Whether the month rows span the advertiser's whole program, which is what a
 * lifetime roll-up needs. The real-brand variants store only a trailing window
 * of a longer tenure — Elana's 13 rows hold $14.0k of a $31.2k lifetime — so
 * summing them would report a third of the program as its total. Their true
 * lifetime figures live on `overview` instead, which the month rows can't
 * reconstruct. Synthetic profiles have no history beyond the months they
 * generate, so for them the rows are the lifetime.
 */
export function hasLifetimeMonthCoverage(): boolean {
  return !isRealBrandProfile(getActiveProfileToken());
}

export function getP8OverviewLifetimeLabel(): string {
  const campaigns = getP8Campaigns();
  const starts = campaigns
    .map((campaign) => campaign.startedRunningAt)
    .filter((value): value is string => Boolean(value))
    .sort();
  const start = starts[0] ?? "2023-10-01";
  const activeToken = getActiveProfileToken();
  const end = isRealBrandProfile(activeToken)
    ? getRealBrandAnchorLabel(activeToken)
    : "Jul 10, 2026";
  return `${formatDisplayDate(start)} – ${end}`;
}
